import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { setMentoringRouteLocale } from '../../i18n';
import { getMentoringContent, getMentoringSharedContent } from './mentoringContent';
import { resolveMentoringRoute } from './mentoringRoutes';
import { MentoringLocaleContext } from './mentoringLocale';
import { MentoringFooter, MentoringHeader } from './components/MentoringLayout';
import { Marquee } from './components/MentoringPrimitives';
import MentoringIndexPage from './pages/MentoringIndexPage';
import MentoringProgramPage from './pages/MentoringProgramPage';
import MentoringProjectsPage from './pages/MentoringProjectsPage';
import MentoringAboutPage from './pages/MentoringAboutPage';
import './mentoring.css';

const PAGE_COMPONENTS = {
  index: MentoringIndexPage,
  program: MentoringProgramPage,
  projects: MentoringProjectsPage,
  about: MentoringAboutPage,
};

/**
 * Locale for the section is path-authoritative (`/fr/mentorat` vs
 * `/en/mentoring`). i18n is realigned to the path on every navigation so
 * shared components (header language state, SEO) agree with the URL.
 */
const synchronizeLocale = async (locale) => {
  try {
    await setMentoringRouteLocale(locale);
  } catch {
    // The document lang is set synchronously by the setter before the async
    // language change, so a detector failure is non-fatal here.
  }
};

/**
 * `public/index.html` ships a site-wide canonical pointing at the homepage.
 * react-helmet-async adds the section's own canonical but does not know about
 * that static node, so without this the document would carry two conflicting
 * canonicals. Helmet tags the nodes it owns with `data-rh`, so anything without
 * that marker is the stale shell node and is safe to drop.
 */
const removeStrayCanonicals = () => {
  [...document.head.querySelectorAll('link[rel="canonical"]')]
    .filter((link) => !link.hasAttribute('data-rh'))
    .forEach((link) => link.remove());
};

export default function MentoringRoute() {
  const location = useLocation();
  const route = resolveMentoringRoute(location.pathname);
  const [readyPath, setReadyPath] = useState();
  const current = useRef(true);

  useEffect(() => {
    current.current = true;
    setReadyPath(undefined);

    synchronizeLocale(route.locale).then(() => {
      if (current.current) {
        setReadyPath(location.pathname);
      }
    });

    return () => {
      current.current = false;
    };
  }, [location.pathname, route.locale]);

  // Runs after every render so the Helmet canonical exists before we prune.
  useEffect(removeStrayCanonicals);

  const content = getMentoringContent(route.locale);
  const shared = getMentoringSharedContent(route.locale);
  const PageComponent = PAGE_COMPONENTS[route.pageKey];

  if (route.type !== 'page' || !content || !shared || !PageComponent) {
    return null;
  }

  const showPage = readyPath === location.pathname;

  return (
    <MentoringLocaleContext.Provider value={route.locale}>
      <div className="mentoring-page">
        <a href="#mentoring-main" className="mt-skip">
          {content.nav.skipToContent}
        </a>

        <MentoringHeader locale={route.locale} nav={content.nav} />

        <main id="mentoring-main">
          {showPage ? <PageComponent locale={route.locale} /> : null}
        </main>

        <Marquee items={shared.marquee} tone="ink" />
        <MentoringFooter footer={shared.footer} nav={content.nav} locale={route.locale} />
      </div>
    </MentoringLocaleContext.Provider>
  );
}
